import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X } from 'phosphor-react';
import { DailyTask } from '../../hooks/useDailyRoutines';
import { toUniversalLink, withPresentOverWebView } from '../../utils/deeplinkMapper';
import { useTranslation } from '../../i18n';
import {
  trackTaskOpened,
  trackTaskCompleted,
  mapTaskType,
  mapTimeCategoryToTime,
  mapContentCategoryToContentType,
  type TaskProps,
} from '../../services/analytics.service';

function buildTaskProps(task: DailyTask): TaskProps {
  return {
    type: mapTaskType({
      type: task.type,
      deeplink: task.deeplink,
      contentCategory: (task as any).contentCategory,
    }),
    contentType: mapContentCategoryToContentType(
      (task as any).contentCategory || task.contentType
    ),
    contentID: (task as any).contentId || (task as any).content_id || null,
    taskTitle: task.title || '',
    time: mapTimeCategoryToTime(task.timeCategory),
  };
}

interface TaskDetailSheetProps {
  task: DailyTask | null;
  token?: string;
  onClose: () => void;
  onMarkDone: () => void;
}

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  z-index: 1100;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Sheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 375px;
  margin: 0 auto;
  background: rgba(34, 34, 34, 0.95);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid var(--color-border-subtle);
  border-bottom: none;
  border-radius: 16px 16px 0 0;
  padding: 20px 20px 40px;
  z-index: 1101;
  animation: ${slideUp} 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 24px;
`;

const CloseButton = styled.button`
  position: absolute;
  left: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--color-border-subtle);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  padding: 0;

  &:active {
    background: var(--color-border);
  }
`;

const HeaderTitle = styled.span`
  font-family: 'Hanken Sans', sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  color: white;
`;

const TaskTitle = styled.h2`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: white;
  margin: 0 0 12px 0;
`;

const TaskDescription = styled.p`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-text-secondary);
  margin: 0 0 20px 0;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 20px;
  gap: 12px;
`;

const ContentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const ContentTitle = styled.span`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #c19fec;
`;

const ContentType = styled.span`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const OpenButton = styled.a`
  background: var(--color-primary);
  color: white;
  font-family: 'Hanken Sans', sans-serif;
  font-weight: 500;
  font-size: 16px;
  border-radius: 100px;
  padding: 8px 20px;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:active {
    opacity: 0.85;
  }
`;

const MarkDoneButton = styled.button<{ $done: boolean }>`
  width: 100%;
  padding: 16px;
  border-radius: 100px;
  background: ${({ $done }) => ($done ? 'var(--color-border-subtle)' : 'var(--color-primary)')};
  border: none;
  color: white;
  font-family: 'Hanken Sans', sans-serif;
  font-weight: 500;
  font-size: 16px;
  letter-spacing: 0.5px;
  cursor: ${({ $done }) => ($done ? 'default' : 'pointer')};
  transition: background 0.2s ease;

  &:active {
    opacity: ${({ $done }) => ($done ? 1 : 0.85)};
  }
`;

const TASK_TYPE_HINT_MAP: Record<string, string> = {
  meditation: 'task.hint.meditation',
  breathing: 'task.hint.breathing',
  yoga: 'task.hint.yoga',
  journal: 'task.hint.journal',
  mood: 'task.hint.mood',
  content: 'task.hint.content',
  activity: 'task.hint.activity',
  session: 'task.hint.session',
  music: 'task.hint.content',
  social: 'task.hint.social',
  general: 'task.hint.general',
};

const CONTENT_TYPE_I18N_MAP: Record<string, string> = {
  meditation: 'content.meditation',
  story: 'content.story',
  music: 'content.music',
  blog: 'content.article',
  talk: 'content.article',
  yoga: 'content.yoga',
  theme: 'content.theme',
  playlist: 'content.playlist',
  category: 'content.category',
};

function getDeeplinkUrl(deeplink: string | undefined, token?: string): string | undefined {
  const url = toUniversalLink(deeplink);
  if (!url) return undefined;
  if (url.includes('wellnesspass-app.meditopia.com')) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}token=${encodeURIComponent(token || '')}&theme=dark`;
  }
  return withPresentOverWebView(url);
}

export const TaskDetailSheet: React.FC<TaskDetailSheetProps> = ({ task, token, onClose, onMarkDone }) => {
  const { t } = useTranslation();
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isOpen = task !== null;

  // Close on escape key (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  if (!task) return null;

  const openUrl = getDeeplinkUrl(task.deeplink, token);
  const contentTypeLabel = task.contentType
    ? t(CONTENT_TYPE_I18N_MAP[task.contentType.toLowerCase()] || '') || task.contentType
    : null;

  return (
    <>
      <Overlay onClick={onClose} />
      <Sheet
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-detail-title"
        tabIndex={-1}
      >
        <Header>
          <CloseButton onClick={onClose} aria-label="Close">
            <X size={18} weight="bold" />
          </CloseButton>
          <HeaderTitle id="task-detail-title">{t('task.sheetTitle')}</HeaderTitle>
        </Header>

        <TaskTitle>{task.title}</TaskTitle>

        {task.description && (
          <TaskDescription>{task.description}</TaskDescription>
        )}

        {openUrl && (
          <ContentRow>
            <ContentInfo>
              {task.contentTitle ? (
                <>
                  <ContentTitle>{task.contentTitle}</ContentTitle>
                  {contentTypeLabel && <ContentType>{contentTypeLabel}</ContentType>}
                </>
              ) : (
                <ContentTitle>
                  {t(TASK_TYPE_HINT_MAP[task.type] || 'task.hint.general')}
                </ContentTitle>
              )}
            </ContentInfo>
            <OpenButton
              href={openUrl}
              onClick={() => trackTaskOpened(buildTaskProps(task))}
            >
              {t('routines.open').toUpperCase()}
            </OpenButton>
          </ContentRow>
        )}

        <MarkDoneButton $done={task.completed} aria-disabled={task.completed} onClick={!task.completed ? () => { trackTaskCompleted(buildTaskProps(task)); onMarkDone(); } : undefined}>
          {task.completed ? t('task.alreadyDone') : t('task.markDone')}
        </MarkDoneButton>
      </Sheet>
    </>
  );
};
